import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BannerTile, CampaignGroupSlide, Carousel, CarouselVariant, Link, LinkVariant, SliderNavColor, SliderNavVariant, Tag, TagColor, Text, TextAlign, useMediaQuery, TextColor, TextType, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { CampaignGroupOpenAs, CampaignGroupTileType, } from '@as-react/common-types';
import { fixProductColoursFallback } from '@as-react/common-utils';
import { useIsClient } from '@yonderland/andes-react';
import { useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop, Box, TextAlpha } from '@yonderland/andes-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeliveryPromiseAPI from '../../api/DeliveryPromiseAPI';
import { MIN_SCREEN_WIDTH_DESKTOP, MIN_SCREEN_WIDTH_DESKTOP_LARGE, MIN_SCREEN_WIDTH_MOBILE_LARGE, MIN_SCREEN_WIDTH_TABLET, MIN_SCREEN_WIDTH_TABLET_SMALL, MAX_SCREEN_WIDTH_MOBILE_LARGE, MAX_SCREEN_WIDTH_MOBILE, } from '../../constants/ScreenWidthConstants';
import { SLIDE_SPACING } from '../../constants/SliderConstants';
import { MiniPDPContainer } from '../../containers/MiniPDPContainer';
import MiniPDPMultiContainer from '../../containers/MiniPDPMultiContainer';
import { ProductAddToBasketLocations } from '../../enums/ActionEnums';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { basketActions } from '../../redux/basket/actions/BasketActions.creators';
import compareActions from '../../redux/compare/actions/CompareActions.creators';
import { makeGetCompareItems } from '../../redux/compare/selectors/CompareSelectors';
import { wishListActions } from '../../redux/wishList/actions/WishListActions.creators';
import { makeGetWishListProducts } from '../../redux/wishList/selectors/WishListSelectors';
import { getImageFromCdn } from '../../util/ImageUtil';
import { isClickAndCollectOnly, isInStoreOnly } from '../../util/ProductUtil';
import { ProductTileVertical } from '../product-tile/vertical/ProductTileVertical';
const CampaignGroupSlideRender = ({ cdnSettings, tile, isMobileSmall, shouldMaintainAspectRatio, setMiniPDPProduct, handleMultiMiniPDPOpen, productTileFormat, compareItems, showCompare, deliveryPromiseExplanations, }) => {
    var _a;
    const { servicesEndpoint, authenticationConfig, imageCdnPrefix, feature_toggles: { lowest_price_all }, } = useAemConfig();
    const dispatch = useDispatch();
    const wishListProducts = useSelector(makeGetWishListProducts());
    const [deliveryPromise, setDeliveryPromise] = useState();
    const product = ((_a = tile.fallbackProducts) === null || _a === void 0 ? void 0 : _a.length) ? fixProductColoursFallback(tile.product) : tile.product;
    const updatedProduct = product
        ? Object.assign(Object.assign({}, product), { colours: product.colours.map(colour => (Object.assign(Object.assign({}, colour), { inWishList: !!wishListProducts.find(wishListProduct => wishListProduct.colorId === colour.colourId && wishListProduct.productId === product.productId) }))) }) : null;
    const handleClick = (e) => {
        if (tile.openAs === CampaignGroupOpenAs.LINK) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (tile.openAs === CampaignGroupOpenAs.MINI_PDP_MULTI) {
            handleMultiMiniPDPOpen(tile);
        }
        else {
            setMiniPDPProduct(updatedProduct);
        }
    };
    const handleKeyUp = (e) => {
        if (tile.openAs === CampaignGroupOpenAs.LINK || e.key !== 'Enter') {
            return;
        }
        setMiniPDPProduct(updatedProduct);
    };
    const handleWishListToggle = () => {
        dispatch(wishListActions.toggleProduct({
            colorId: updatedProduct.colourId,
            productCode: updatedProduct.productCode,
            productId: updatedProduct.productId,
        }));
    };
    const handleAddToBasket = (productData) => {
        dispatch(basketActions.createAddExtensiveTileProductAction(productData, ProductAddToBasketLocations.CAMPAIGN_GROUP));
    };
    const handleUpdateCompare = (compareProduct) => {
        dispatch(compareActions.createUpdateOnProductDetailPageAction(compareProduct));
    };
    const fetchDeliveryPromise = (_a) => __awaiter(void 0, [_a], void 0, function* ({ skuId, productId, }) {
        const fetchedDeliveryPromise = yield DeliveryPromiseAPI.getDeliveryPromise(servicesEndpoint, skuId, productId, authenticationConfig);
        !!fetchedDeliveryPromise && setDeliveryPromise(fetchedDeliveryPromise);
    });
    switch (tile.type) {
        case CampaignGroupTileType.TEXT:
            return (_jsxs(CampaignGroupSlide, { shouldMaintainAspectRatio: shouldMaintainAspectRatio, isMobileSmall: isMobileSmall, children: [tile.openAs === CampaignGroupOpenAs.LINK && (_jsx(Link, { href: tile.href, variant: LinkVariant.CONTAINER, children: _jsx(Text, { html: tile.text }) })), tile.openAs === CampaignGroupOpenAs.MINI_PDP && (_jsx("div", { role: "button", tabIndex: 0, onClick: handleClick, onKeyUp: handleKeyUp, children: _jsx(Text, { html: tile.text }) })), tile.openAs === CampaignGroupOpenAs.NONE && _jsx(Text, { html: tile.text })] }));
        case CampaignGroupTileType.IMAGE: {
            const image = tile.openAs === CampaignGroupOpenAs.MINI_PDP_MULTI
                ? tile.images.find(item => item.isDefault) || tile.images[0]
                : tile.image;
            const imageUrl = getImageFromCdn(image, imageCdnPrefix, undefined, cdnSettings === null || cdnSettings === void 0 ? void 0 : cdnSettings.imageQuality, cdnSettings === null || cdnSettings === void 0 ? void 0 : cdnSettings.requestPassThrough);
            return (_jsx(CampaignGroupSlide, { shouldMaintainAspectRatio: shouldMaintainAspectRatio, isMobileSmall: isMobileSmall, children: _jsx(BannerTile, { onClick: handleClick, url: tile.href, src: imageUrl, tags: tile.imageLabel && _jsx(Tag, { color: TagColor.GENERAL, children: tile.imageLabel }) }) }));
        }
        case CampaignGroupTileType.PRODUCT:
        default:
            return updatedProduct ? (_jsx(CampaignGroupSlide, { isMobileSmall: isMobileSmall, children: _jsx(ProductTileVertical, { product: Object.assign(Object.assign({}, updatedProduct), { deliveryPromise: deliveryPromise }), format: productTileFormat, onTileClick: handleClick, onWishListToggle: handleWishListToggle, addToBasket: handleAddToBasket, showColorOutOfStockOverlay: true, showProductOutOfStockOverlay: true, additionalQueryParameters: tile.queryParameters, inStoreOnly: isInStoreOnly(updatedProduct), clickAndCollectOnly: isClickAndCollectOnly(updatedProduct), getDeliveryPromise: fetchDeliveryPromise, deliveryPromiseExplanations: deliveryPromiseExplanations, dropdownProps: { appendToBody: true }, updateCompareList: handleUpdateCompare, compareItems: compareItems, showComparison: showCompare, showComparisonOnAllPages: true, showCompareForExtensiveTile: showCompare, adjustDescriptionHeight: productTileFormat === 'simple', description: productTileFormat === 'simple' &&
                        tile.shortDescription && (_jsx(Text, { color: TextColor.SUBTLE, type: TextType.CONTENT, whiteSpace: "pre-wrap", children: tile.shortDescription })), enablePriceReference: lowest_price_all }) })) : (_jsx(CampaignGroupSlide, { shouldMaintainAspectRatio: shouldMaintainAspectRatio, isMobileSmall: isMobileSmall }));
    }
};
const CampaignGroup = ({ hideForDesktop, hideOnViewports, innerSpacing, displayArrows, showIndicators, slidesToScroll, slidesToShow, title, subTitle, type, tiles, deliveryPromiseExplanations, cdnSettings, mainWebshopId, productTileFormat, showCompare, }) => {
    const isClient = useIsClient();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const isTabletSmallAndBellow = useMediaQueryMatchesOnMobile();
    const isMobileLarge = useMediaQuery(`(min-width: ${MIN_SCREEN_WIDTH_MOBILE_LARGE}px) and (max-width: ${MAX_SCREEN_WIDTH_MOBILE_LARGE}px)`);
    const isMobile = useMediaQuery(`(max-width: ${MAX_SCREEN_WIDTH_MOBILE}px)`);
    const [miniPDPProduct, setMiniPDPProduct] = useState(null);
    const [activeMultiTile, setActiveMultiTile] = useState(null);
    const [isOpenMultiMiniPDP, setIsOpenMultiMiniPDP] = useState(false);
    const compareItems = useSelector(makeGetCompareItems());
    const dispatch = useDispatch();
    if (!isClient)
        return null;
    const isMobileOrMobileLarge = isMobile || isMobileLarge;
    if ((hideForDesktop && !isTabletSmallAndBellow) || (hideOnViewports && isTabletSmallAndBellow)) {
        return null;
    }
    const isFloating = type === 'floating';
    const getShowPagination = () => {
        if (!showIndicators) {
            if (!isFloating || (isFloating && isDesktop)) {
                return false;
            }
            // Show indicator for non desktop floating sliders
            return true;
        }
        return showIndicators && isFloating;
    };
    const shouldCenterSlides = () => {
        // It should center slides when its mobile or mobile large,
        // it is a floating carousel and only 1 slide needs to be shown.
        if (!isMobileOrMobileLarge || !isFloating) {
            return false;
        }
        return (isMobileLarge && slidesToShow.mobileLarge === 1) || (isMobile && slidesToShow.mobile === 1);
    };
    const showNavigation = !isFloating || (isDesktop && displayArrows);
    const showPagination = getShowPagination();
    const shouldMaintainAspectRatio = !tiles.some(tile => tile.type === CampaignGroupTileType.PRODUCT && tile.product);
    const campaignGroupSliderCarousel = {
        variant: CarouselVariant.CAMPAIGN_GROUP,
        showPagination,
        arrowsConfig: {
            variant: isMobileOrMobileLarge ? SliderNavVariant.SEMICIRCLE : SliderNavVariant.CIRCLE,
            color: SliderNavColor.PRIMARY,
        },
        configurationOverrides: {
            preventClicks: true,
            slidesPerView: isFloating ? slidesToShow.mobile + 0.3 : slidesToShow.mobile,
            slidesPerGroup: slidesToScroll.mobile,
            spaceBetween: innerSpacing ? SLIDE_SPACING.MOBILE : 0,
            watchOverflow: true,
            shouldSwiperUpdate: true,
            centeredSlides: shouldCenterSlides(),
            breakpoints: {
                [MIN_SCREEN_WIDTH_MOBILE_LARGE]: {
                    spaceBetween: innerSpacing ? SLIDE_SPACING.MOBILE : 0,
                    slidesPerView: isFloating ? slidesToShow.mobileLarge + 0.3 : slidesToShow.mobileLarge,
                    slidesPerGroup: slidesToScroll.mobileLarge,
                },
                [MIN_SCREEN_WIDTH_TABLET_SMALL]: {
                    spaceBetween: innerSpacing ? SLIDE_SPACING.TABLET : 0,
                    slidesPerView: isFloating ? slidesToShow.tabletSmall + 0.3 : slidesToShow.desktop,
                    slidesPerGroup: slidesToScroll.tabletSmall,
                },
                [MIN_SCREEN_WIDTH_TABLET]: {
                    spaceBetween: innerSpacing ? SLIDE_SPACING.TABLET : 0,
                    slidesPerView: isFloating ? slidesToShow.tablet + 0.6 : slidesToShow.tablet,
                    slidesPerGroup: slidesToScroll.tablet,
                },
                [MIN_SCREEN_WIDTH_DESKTOP]: {
                    spaceBetween: innerSpacing ? SLIDE_SPACING.TABLET : 0,
                    slidesPerView: isFloating ? slidesToShow.desktop + 0.6 : slidesToShow.desktop,
                    slidesPerGroup: slidesToScroll.desktop,
                },
                [MIN_SCREEN_WIDTH_DESKTOP_LARGE]: {
                    spaceBetween: innerSpacing ? SLIDE_SPACING.TABLET : 0,
                    slidesPerView: isFloating ? slidesToShow.desktopLarge + 0.6 : slidesToShow.desktopLarge,
                    slidesPerGroup: slidesToScroll.desktopLarge,
                },
            },
        },
    };
    if (!showNavigation) {
        campaignGroupSliderCarousel.configurationOverrides.navigation = false;
    }
    const handleMiniPDPClose = () => {
        setMiniPDPProduct(null);
    };
    const handleMultiMiniPDPOpen = (tile) => {
        const defaultImage = tile.images.find(image => image.isDefault) || tile.images[0];
        const image = defaultImage.imagePath.split('/');
        dispatch(analyticsActions.shopTheLookOpened(image[image.length - 1]));
        setActiveMultiTile(tile);
        setIsOpenMultiMiniPDP(true);
    };
    const handleMultiMiniPDPClose = () => {
        setIsOpenMultiMiniPDP(false);
    };
    return (_jsxs(_Fragment, { children: [(title || subTitle) && (_jsxs(Box, { paddingBlockEnd: "6", children: [title && (_jsx(TextAlpha, { as: "h2", alignment: "center", variant: "subheadingL", children: title })), subTitle && _jsx(Text, { align: TextAlign.CENTER, children: subTitle })] })), _jsx(Carousel, Object.assign({}, campaignGroupSliderCarousel, { children: tiles.map((tile, index) => (_jsx(CampaignGroupSlideRender, { tile: tile, isMobileSmall: slidesToShow.mobile > 1, shouldMaintainAspectRatio: shouldMaintainAspectRatio, setMiniPDPProduct: setMiniPDPProduct, handleMultiMiniPDPOpen: handleMultiMiniPDPOpen, cdnSettings: cdnSettings, productTileFormat: productTileFormat, compareItems: compareItems, showCompare: showCompare, deliveryPromiseExplanations: deliveryPromiseExplanations }, `campaign_group_slide_${index}`))) })), _jsx(MiniPDPContainer, { displayPackShotImagesFirst: true, productCode: miniPDPProduct === null || miniPDPProduct === void 0 ? void 0 : miniPDPProduct.productCode, colorId: miniPDPProduct === null || miniPDPProduct === void 0 ? void 0 : miniPDPProduct.colourId, onClose: handleMiniPDPClose, deliveryPromiseExplanations: deliveryPromiseExplanations }), _jsx(MiniPDPMultiContainer, { isOpen: isOpenMultiMiniPDP, onClose: handleMultiMiniPDPClose, productIds: activeMultiTile === null || activeMultiTile === void 0 ? void 0 : activeMultiTile.multipleProducts, extensiveCarouselImages: activeMultiTile === null || activeMultiTile === void 0 ? void 0 : activeMultiTile.images, cdnSettings: cdnSettings, popoverTitle: activeMultiTile === null || activeMultiTile === void 0 ? void 0 : activeMultiTile.title, mainWebshopId: mainWebshopId })] }));
};
export default CampaignGroup;
